// Entry-fee payments client. Thin wrappers over the Stripe Cloud Functions
// (createEntryCheckout / verifyEntryCheckout / refundEntryFee). The browser
// never holds a Stripe secret and never marks an entry paid — it only kicks off
// Checkout and asks the server to confirm. Mirrors the existing callable pattern
// (getFunctions(app,'us-central1') + httpsCallable) used by processAICommand.
import { getFunctions, httpsCallable } from 'firebase/functions'
import { app } from './firebase.js'
import { getActiveClubId } from './tenant.js'

const fns = () => getFunctions(app, 'us-central1')
// Active club (Golf Elite) threaded to the server so it reads/writes the right
// tenant; null on every other build → server uses the global path (unchanged).
const cid = () => getActiveClubId()

// Optional — present only if a publishable key is configured; the redirect flow
// below uses the server-returned Checkout URL and does not require it.
export const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || ''

// Start Checkout for one roster entry → redirect the browser to Stripe. The
// current origin is passed so the server can build same-site return URLs (no
// payment data in them — only the opaque roster-entry id).
export async function startEntryCheckout(tournamentId, regId) {
  const { data } = await httpsCallable(fns(), 'createEntryCheckout')({
    tournamentId, regId, clubId: cid(), origin: window.location.origin,
  })
  if (!data?.url) throw new Error('Could not start checkout.')
  window.location.assign(data.url)
}

// Redirect-return fallback: ask the server to re-check the stored session and
// set paid status authoritatively. Returns { status }.
export async function verifyEntryCheckout(tournamentId, regId) {
  const { data } = await httpsCallable(fns(), 'verifyEntryCheckout')({ tournamentId, regId, clubId: cid() })
  return data || { status: 'none' }
}

// Admin: refund a paid entry via Stripe (test mode). Returns { status, refundId }.
export async function refundEntryFee(tournamentId, regId) {
  const { data } = await httpsCallable(fns(), 'refundEntryFee')({ tournamentId, regId, clubId: cid() })
  return data || { status: 'error' }
}

// ── PayPal / Venmo (Lane 2) ────────────────────────────────────────────────
// Publishable client id (safe in the browser — loads the JS SDK). The secret
// stays server-side in Secret Manager. Empty → PayPal lane can't render.
export const PAYPAL_CLIENT_ID = import.meta.env.VITE_PAYPAL_CLIENT_ID || ''

let _paypalSdk = null
export function loadPaypalSdk(currency = 'USD') {
  if (!PAYPAL_CLIENT_ID) return Promise.reject(new Error('PayPal is not configured.'))
  if (typeof window !== 'undefined' && window.paypal) return Promise.resolve(window.paypal)
  if (_paypalSdk) return _paypalSdk
  _paypalSdk = new Promise((resolve, reject) => {
    const s = document.createElement('script')
    s.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(PAYPAL_CLIENT_ID)}&enable-funding=venmo&currency=${encodeURIComponent(currency)}&components=buttons`
    s.onload = () => resolve(window.paypal)
    s.onerror = () => { _paypalSdk = null; reject(new Error('Could not load PayPal.')) }
    document.head.appendChild(s)
  })
  return _paypalSdk
}

export async function createPaypalOrder(tournamentId, regId) {
  const { data } = await httpsCallable(fns(), 'createPaypalOrder')({ tournamentId, regId, clubId: cid() })
  if (!data?.orderID) throw new Error('Could not start PayPal checkout.')
  return data.orderID
}
export async function capturePaypalOrder(tournamentId, regId, orderID) {
  const { data } = await httpsCallable(fns(), 'capturePaypalOrder')({ tournamentId, regId, orderID, clubId: cid() })
  return data || { status: 'pending' }
}
export async function refundPaypalEntry(tournamentId, regId) {
  const { data } = await httpsCallable(fns(), 'refundPaypalEntry')({ tournamentId, regId, clubId: cid() })
  return data || { status: 'error' }
}

// Format a stored fee ({ amount, currency }) for display, e.g. "$50.00".
export function formatFee(fee) {
  const amt = Number(fee?.amount)
  if (!Number.isFinite(amt) || amt <= 0) return ''
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: (fee.currency || 'USD').toUpperCase() }).format(amt)
  } catch {
    return `$${amt.toFixed(2)}`
  }
}
